import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { BasecampLayout, AreaLayout } from '../../components/Layout';
import { PageHeader, Card, Spinner, Alert, EmptyState, Table, Btn, Input } from '../../components/UI';
import { useMsal } from '@azure/msal-react';
import { api } from '../../utils/api';
import { ToolboxContent } from '../songbook/SongbookPages';
import ObservationPage from './ObservationPage';

// Grow — Faza 2 obim (WP-2): My Certification Path, My Business Growth
// (informativno dok CRM ne stigne u Fazi 3), Feedback & Development Plan
// (linkovi ka compass.hansenbeck.com), Hikes i Expeditions (živi, iz Events).
// Learning Paths / Observation / Content Atlas / Theory → kasnije faze.

const SUBNAV = [
  { href: '/grow/path', label: 'My Certification Path' },
  { href: '/grow/biz', label: 'My Business Growth' },
  { href: '/grow/feedback', label: 'Feedback & Development Plan' },
  { href: '/grow/hikes', label: 'Hikes' },
  { href: '/grow/expeditions', label: 'Expeditions' },
  { href: '/grow/obs', label: 'Observation & Co-delivery' },
  { href: '/grow/atlas', label: 'Hansen Beck Toolbox' },
  { href: '/grow/theory', label: 'Library' },
  { href: '/grow/renovation', label: 'Content by Open Innovation' },
];

function Loading() {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><Spinner /></div>;
}

// ── My Certification Path ─────────────────────────────────────────────────
function Path() {
  return (
    <>
      <Alert type="info">
        The ladder: <b>HB Qualified → Ice Pilot Explorer → Ice Pilot Master</b>, under the vision
        "100% Ice Pilot Masters". Explorer certification is always an onsite event.
      </Alert>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginTop: 20 }}>
        {[
          ['HB Qualified', 'The formation gate — CFA completed, qualification assessment passed.'],
          ['Ice Pilot Explorer', 'The licence to sell and deliver under the Hansen Beck brand. Onsite certification — currently on Expedition.'],
          ['Ice Pilot Master', 'Mastery as the standard: key facets at level 4, mentoring hours, showing the craft by observation.'],
        ].map(([title, text], i) => (
          <Card key={title} style={{ padding: '20px 22px' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.8rem', color: 'var(--ink-faint)', marginBottom: 6 }}>{i + 1}</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 700, marginBottom: 8 }}>{title}</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--ink-soft)', lineHeight: 1.55 }}>{text}</div>
          </Card>
        ))}
      </div>
      <Card style={{ padding: '18px 22px', marginTop: 20 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', marginBottom: 8 }}>My readiness</h3>
        <p style={{ fontSize: '0.86rem', color: 'var(--ink-soft)' }}>
          Readiness vs certification targets is computed from your Compass gap report — see
          {' '}<Link to="/grow/feedback" style={{ textDecoration: 'underline' }}>Feedback & Development Plan</Link>.
          First-year KPIs for new Ice Pilots: companies prospected (with results) and training days delivered.
        </p>
      </Card>
    </>
  );
}

// ── My Business Growth ────────────────────────────────────────────────────
function Biz() {
  return (
    <>
      <Alert type="info">
        Growth is personal and business at once — Basecamp helps with both. Your numbers (prospecting,
        proposals won, delivery days, FAR earned) will read from your own CRM and delivery data once
        Explore/My Projects arrives in Phase 3.
      </Alert>
      <Card style={{ padding: '22px 24px', marginTop: 20 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 12 }}>Help available</h3>
        <div style={{ fontSize: '0.9rem', lineHeight: 2 }}>
          <div>▪ My mentor — see Member Directory</div>
          <div>▪ Business review with my BP — quarterly</div>
          <div>▪ Prospecting method & tools → Explore (Phase 3)</div>
          <div>▪ Reference clients in my vertical → HB Clients registry (Phase 3)</div>
          <div>▪ <Link to="/community/events" style={{ textDecoration: 'underline' }}>Call a Hike</Link> on a business topic (e.g. winning enterprise clients)</div>
        </div>
      </Card>
    </>
  );
}

// ── Feedback & Development Plan ───────────────────────────────────────────
function Feedback() {
  const [sending, setSending] = useState(null); // 'self' | '360'
  const [sent, setSent] = useState(null);       // 'self' | '360'

  async function requestAssessment(type) {
    setSending(type);
    try { await api.requestAssessment(type); setSent(type); }
    catch (e) { alert(e.message); }
    finally { setSending(null); }
  }

  return (
    <>
      <Alert type="info">
        The same engine we sell, pointed inward: self-assessment and 360 run on
        {' '}<b>compass.hansenbeck.com</b> against the Ice Pilot ideal profile. The same gap report
        doubles as certification readiness.
      </Alert>
      <p style={{ fontSize: '0.82rem', color: 'var(--ink-faint)', margin: '12px 0 20px' }}>
        Access to these results is defined by the IP Agreement: you, HB GmbH, your Business Partner,
        and your assigned mentor.
      </p>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Btn loading={sending === 'self'} onClick={() => requestAssessment('self')}>Start self-assessment →</Btn>
        <Btn variant="outline" loading={sending === '360'} onClick={() => requestAssessment('360')}>Start 360 feedback →</Btn>
      </div>
      {sent && (
        <div style={{ marginTop: 14 }}>
          <Alert type="success">
            Your request for a {sent === '360' ? '360 feedback assessment' : 'self-assessment'} has been sent.
            You’ll be contacted to get started.
          </Alert>
        </div>
      )}
      <Card style={{ padding: '18px 22px', marginTop: 24 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', marginBottom: 8 }}>Development plan</h3>
        <p style={{ fontSize: '0.86rem', color: 'var(--ink-soft)' }}>
          Once your gap report is in, the development plan recommends Units, Hikes and Custodian
          observation computed from the same facet tags — coming with the Compass integration (Phase 3).
        </p>
      </Card>
    </>
  );
}

// ── Hikes / Expeditions (iz Events) ───────────────────────────────────────
function EventsView({ kind }) {
  const [state, setState] = useState({ loading: true, error: null, events: [] });
  const [topic, setTopic] = useState('');
  const load = useCallback(() => {
    api.getEvents()
      .then(d => setState({ loading: false, error: null, events: d.events }))
      .catch(e => setState({ loading: false, error: e.message, events: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function signup(id) { try { await api.toggleSignup(id); load(); } catch (e) { alert(e.message); } }
  async function vote(id)   { try { await api.toggleVote(id); load(); }   catch (e) { alert(e.message); } }
  async function callHike(e) {
    e.preventDefault();
    if (!topic.trim()) return;
    try { await api.createEvent({ title: topic, kind: 'hike' }); setTopic(''); load(); }
    catch (err) { alert(err.message); }
  }

  if (state.loading) return <Loading />;
  if (state.error) return <Alert type="error">{state.error}</Alert>;

  const list = state.events.filter(e => e.kind === kind && e.status !== 'requested');
  const requested = kind === 'hike' ? state.events.filter(e => e.status === 'requested') : [];

  return (
    <>
      {kind === 'hike' && (
        <Alert type="info">
          Anyone calls (topic + interest votes); organised on request by Hike administrators or
          strategically by GmbH — every Hike carries a named organiser.
        </Alert>
      )}
      <Card style={{ padding: '18px 22px', marginTop: 16 }}>
        <Table
          headers={['Event', 'When', 'Organiser', 'Sign-up']}
          rows={list.map(e => [
            <span><b>{e.title}</b><br /><span style={{ fontSize: '0.78rem', color: 'var(--ink-faint)' }}>{e.about}</span></span>,
            `${e.when || 'tba'}${e.format ? ` · ${e.format}` : ''}`,
            e.organiser || 'to be assigned',
            e.organiser
              ? <Btn size="sm" variant={e.signedUp ? 'primary' : 'outline'} onClick={() => signup(e.id)}>{e.signedUp ? 'Signed up ✓' : 'Sign up'}</Btn>
              : <span style={{ color: 'var(--ink-faint)', fontSize: '0.8rem' }}>soon</span>,
          ])}
          emptyMessage="Nothing scheduled yet"
        />
      </Card>
      {kind === 'hike' && (
        <Card style={{ padding: '18px 22px', marginTop: 18 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', marginBottom: 10 }}>Call for a Hike</h3>
          <form onSubmit={callHike} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            <Input placeholder="Propose a topic…" value={topic} onChange={e => setTopic(e.target.value)} style={{ flex: 1, minWidth: 200 }} />
            <Btn type="submit">Call for a Hike</Btn>
          </form>
          {requested.length > 0 && (
            <Table
              headers={['Topic', 'Initiated by', 'Interested', '']}
              rows={requested.map(h => [
                h.title, h.initiatedBy || '—', h.votes,
                <Btn size="sm" variant={h.voted ? 'primary' : 'outline'} onClick={() => vote(h.id)}>{h.voted ? 'Interested ✓' : "I'm interested"}</Btn>,
              ])}
            />
          )}
        </Card>
      )}
    </>
  );
}

// ── Expeditions ───────────────────────────────────────────────────────────
// Ne "event sa sign-up" — Expeditions samo vodi na expedition sajt + pokazuje
// kada je sledeća ekspedicija i (opciono) ubaci datume u kalendar ulogovanog
// Microsoft naloga (Graph /me/events preko MSAL tokena). Sign-up / prijave žive
// na expedition.hansenbeck.com, ne ovde.
const EXPEDITION = {
  name: 'Expedition 15',
  site: 'https://expedition.hansenbeck.com/',
  when: 'Sun 23 – Fri 28 August 2026',
  startDate: '2026-08-23',
  endDateExclusive: '2026-08-29', // all-day event end is exclusive → covers through the 28th
  timeZone: 'Europe/Belgrade',
  schedule: [
    ['Sun, 23 Aug', 'Arrival · Welcome Drinks (19:00) · Golden 20s reveal'],
    ['Mon 24 – Thu 27 Aug', 'Group work · Standing Ovation presentations on Wed, 26 Aug'],
    ['Fri, 28 Aug', 'Open Innovation day · Gala Night'],
  ],
};

function expeditionDescription() {
  return [
    `${EXPEDITION.name} — ${EXPEDITION.when}`,
    '',
    ...EXPEDITION.schedule.map(([d, t]) => `• ${d}: ${t}`),
    '',
    EXPEDITION.site,
  ].join('\n');
}

function downloadExpeditionIcs() {
  const toStamp = (d) => `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(d.getUTCDate()).padStart(2, '0')}T${String(d.getUTCHours()).padStart(2, '0')}${String(d.getUTCMinutes()).padStart(2, '0')}${String(d.getUTCSeconds()).padStart(2, '0')}Z`;
  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Hansen Beck//Basecamp//EN', 'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    'UID:expedition-15@hansenbeck.com',
    `DTSTAMP:${toStamp(new Date())}`,
    `DTSTART;VALUE=DATE:${EXPEDITION.startDate.replace(/-/g, '')}`,
    `DTEND;VALUE=DATE:${EXPEDITION.endDateExclusive.replace(/-/g, '')}`,
    `SUMMARY:${EXPEDITION.name}`,
    `DESCRIPTION:${expeditionDescription().replace(/\n/g, '\\n')}`,
    `URL:${EXPEDITION.site}`,
    'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
  const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
  const a = document.createElement('a');
  a.href = url; a.download = 'expedition-15.ics';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function ExpeditionsView() {
  const { instance, accounts } = useMsal();
  const [cal, setCal] = useState({ status: 'idle', message: null }); // idle | working | done | error

  async function addToCalendar() {
    setCal({ status: 'working', message: null });
    try {
      const account = instance.getActiveAccount() || accounts[0];
      if (!account) throw new Error('No signed-in Microsoft account.');
      const scopes = ['Calendars.ReadWrite'];
      let token;
      try {
        token = (await instance.acquireTokenSilent({ scopes, account })).accessToken;
      } catch {
        token = (await instance.acquireTokenPopup({ scopes, account })).accessToken;
      }
      const res = await fetch('https://graph.microsoft.com/v1.0/me/events', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: EXPEDITION.name,
          isAllDay: true,
          // A fixed transactionId makes repeat clicks idempotent (Graph won't duplicate).
          transactionId: 'hb-expedition-15',
          body: { contentType: 'text', content: expeditionDescription() },
          start: { dateTime: `${EXPEDITION.startDate}T00:00:00`, timeZone: EXPEDITION.timeZone },
          end: { dateTime: `${EXPEDITION.endDateExclusive}T00:00:00`, timeZone: EXPEDITION.timeZone },
        }),
      });
      if (res.status === 201 || res.status === 409) {
        // 409 = same transactionId already added → treat as success (no duplicate).
        setCal({ status: 'done', message: 'Added to your Outlook calendar ✓' });
      } else {
        const txt = await res.text().catch(() => '');
        throw new Error(`Graph ${res.status}${txt ? `: ${txt.slice(0, 140)}` : ''}`);
      }
    } catch (e) {
      setCal({ status: 'error', message: e.message || 'Could not reach your calendar.' });
    }
  }

  return (
    <>
      <Alert type="info">
        Expeditions are our onsite gatherings. Everything — the full programme, your groups, Open
        Innovation and sign-ups — lives on the Expedition site. This tab just points you there and
        keeps the dates handy.
      </Alert>

      <Card style={{ padding: '22px 24px', marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem', margin: 0 }}>{EXPEDITION.name}</h3>
          <span style={{ color: 'var(--ink-soft)', fontSize: '0.9rem' }}>{EXPEDITION.when}</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '18px 0' }}>
          {EXPEDITION.schedule.map(([day, text]) => (
            <div key={day} style={{ display: 'flex', gap: 14, fontSize: '0.9rem', lineHeight: 1.5 }}>
              <div style={{ flex: '0 0 150px', fontWeight: 700 }}>{day}</div>
              <div style={{ color: 'var(--ink-soft)' }}>{text}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
          <a href={EXPEDITION.site} target="_blank" rel="noreferrer"
            style={{ display: 'inline-block', background: 'var(--ink)', color: '#fff', padding: '10px 18px', borderRadius: 'var(--radius-md)', fontSize: '0.88rem', fontWeight: 600, textDecoration: 'none' }}>
            Open the Expedition site →
          </a>
          <Btn variant="outline" loading={cal.status === 'working'} disabled={cal.status === 'done'} onClick={addToCalendar}>
            {cal.status === 'done' ? 'In your calendar ✓' : 'Add to my calendar'}
          </Btn>
          <button onClick={downloadExpeditionIcs}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--ink-soft)', textDecoration: 'underline', fontSize: '0.82rem', fontFamily: 'var(--font-body)' }}>
            or download .ics
          </button>
        </div>

        {cal.status === 'error' && (
          <div style={{ marginTop: 12 }}>
            <Alert type="warn">
              Couldn’t add it to your Microsoft calendar ({cal.message}). Use “download .ics” instead, or ask an
              admin to grant Base Camp calendar access.
            </Alert>
          </div>
        )}
        {cal.status === 'done' && (
          <p style={{ marginTop: 12, fontSize: '0.82rem', color: 'var(--teal)' }}>{cal.message}</p>
        )}
      </Card>
    </>
  );
}

// ── Content by Open Renovation — svi odobreni predlozi sadržaja (commons) ──
function ContentRenovation() {
  const [state, setState] = useState({ loading: true, error: null, proposals: [] });
  useEffect(() => {
    api.getApprovedContent()
      .then(d => setState({ loading: false, error: null, proposals: d.proposals }))
      .catch(e => setState({ loading: false, error: e.message, proposals: [] }));
  }, []);

  if (state.loading) return <Loading />;
  if (state.error) return <Alert type="error">{state.error}</Alert>;

  return (
    <>
      <Alert type="info">
        Community-renovated content — new material proposed by Ice Pilots and approved by Custodians.
        Shared HB property, credited to the creator, reusable by all.
      </Alert>
      {state.proposals.length === 0 ? (
        <div style={{ marginTop: 16 }}>
          <EmptyState icon="🌐" title="Nothing yet" message="Approved content proposals will appear here." />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14, marginTop: 18 }}>
          {state.proposals.map(p => (
            <Card key={p.id} style={{ padding: '16px 20px' }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{p.title}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--ink-faint)', marginTop: 2 }}>
                {p.area}{p.submittedBy ? ` · by ${p.submittedBy}` : ''}
              </div>
              {p.description && <div style={{ fontSize: '0.85rem', color: 'var(--ink-soft)', marginTop: 8, lineHeight: 1.5 }}>{p.description}</div>}
              {p.hasFile && (
                <button
                  onClick={() => api.downloadProposalFile(p.id, p.fileName).catch(e => alert(e.message))}
                  style={{ marginTop: 10, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--ink)', textDecoration: 'underline', fontSize: '0.82rem', fontFamily: 'var(--font-body)' }}
                >
                  ↓ {p.fileName}
                </button>
              )}
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

const COMING = {
  theory: ['Library', 'The reading ladder. Coming in Phase 4.'],
};

const TITLES = {
  path: 'My Certification Path', biz: 'My Business Growth', feedback: 'Feedback & Development Plan',
  hikes: 'Hikes', expeditions: 'Expeditions', learn: 'Learning Paths',
  obs: 'Observation & Co-delivery', atlas: 'Hansen Beck Toolbox', theory: 'Library',
  renovation: 'Content by Open Innovation',
};

export default function Grow() {
  const { sub } = useParams();
  if (!sub) return <Navigate to="/grow/path" replace />;

  let body;
  if (sub === 'path') body = <Path />;
  else if (sub === 'biz') body = <Biz />;
  else if (sub === 'feedback') body = <Feedback />;
  else if (sub === 'hikes') body = <EventsView kind="hike" />;
  else if (sub === 'expeditions') body = <ExpeditionsView />;
  else if (sub === 'atlas') body = <ToolboxContent />;
  else if (sub === 'obs') body = <ObservationPage />;
  else if (sub === 'renovation') body = <ContentRenovation />;
  else if (COMING[sub]) body = <EmptyState icon="🏔" title={COMING[sub][0]} message={COMING[sub][1]} />;
  else return <Navigate to="/grow/path" replace />;

  return (
    <BasecampLayout>
      <PageHeader title="Grow — develop yourself" subtitle={TITLES[sub]} />
      <AreaLayout items={SUBNAV}>{body}</AreaLayout>
    </BasecampLayout>
  );
}
